import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import * as resellerService from "@/shared/api/services/resellerService";
import * as transactionService from "@/shared/api/services/transactionService";
import { mapGlobalTransactions } from "@/shared/utils/transactionMappers";
import { format } from "date-fns";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Calendar } from "@/shared/components/ui/calendar";
import {
  Search,
  Calendar as CalendarIcon,
  Download,
  X,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import ResellerLayout from "@/shared/components/reseller/ResellerLayout";
import TransactionDetailPanel from "@/shared/components/merchant/TransactionDetailPanel";
import TransactionTable from "@/shared/components/transactions/TransactionTable";

export default function ResellerTransactions() {
  const { t } = useLanguage();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [currencyFilter, setCurrencyFilter] = useState("all");
  const [merchantFilter, setMerchantFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [selectedTx, setSelectedTx] = useState(null);
  const [page, setPage] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pageSize = 10;

  const resellerName = "Reseller Portal";

  const { data: merchants = [], isLoading: merchantsLoading } = useQuery({
    queryKey: ["reseller-merchants"],
    queryFn: resellerService.getMyMerchants,
  });

  const merchantIds = useMemo(() => {
    return merchants.map(m => m.id);
  }, [merchants]);

  const { data: paginationResult, isLoading: txLoading, refetch: refetchTransactions } = useQuery({
    queryKey: ["reseller-transactions", page, pageSize, search, statusFilter, typeFilter, currencyFilter, merchantFilter, dateRange, merchantIds],
    queryFn: () => {
      const backendFilters = {};
      if (statusFilter !== "all") backendFilters.status = statusFilter;
      if (typeFilter !== "all") backendFilters.type = typeFilter.toLowerCase();
      if (currencyFilter !== "all") backendFilters.currency = currencyFilter;
      if (dateRange.from) backendFilters.startDate = dateRange.from.toISOString();
      if (dateRange.to) {
        const endOfToDay = new Date(dateRange.to);
        endOfToDay.setHours(23, 59, 59, 999);
        backendFilters.endDate = endOfToDay.toISOString();
      }

      const mIds = merchantFilter !== "all" ? [merchantFilter] : merchantIds;

      return transactionService.getPaginatedTransactions(
        page + 1,
        pageSize,
        search,
        backendFilters,
        mIds.length > 0 ? mIds : ["_none_"]
      );
    },
    enabled: !merchantsLoading,
    keepPreviousData: true,
  });

  const isLoading = merchantsLoading || txLoading;

  const rawTransactions = paginationResult?.data || [];
  const totalTransactions = paginationResult?.total || 0;
  const totalPages = paginationResult?.totalPages || 1;

  const filteredTransactions = useMemo(() => {
    return mapGlobalTransactions(rawTransactions);
  }, [rawTransactions]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetchTransactions();
    } finally {
      setIsRefreshing(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setTypeFilter("all");
    setCurrencyFilter("all");
    setMerchantFilter("all");
    setDateRange({ from: null, to: null });
    setPage(0);
  };

  const hasFilters = search || statusFilter !== "all" || typeFilter !== "all" || currencyFilter !== "all" || merchantFilter !== "all" || dateRange.from;

  return (
    <ResellerLayout currentPage="ResellerTransactions" resellerName={resellerName}>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t("transactions")}</h1>
            <p className="text-slate-500">{totalTransactions.toLocaleString()} {t("transactions")}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={handleRefresh} disabled={isRefreshing} className="bg-white">
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
            </Button>
            <Button variant="outline" className="gap-2">
              <Download className="h-4 w-4" />
              {t("export")}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700">{t("filters") || "Filters"}</h3>
            {hasFilters && (
              <Button variant="ghost" onClick={clearFilters} className="text-slate-500 gap-2 h-8 hover:bg-slate-50">
                <X className="h-4 w-4" />
                <span className="text-sm font-medium">{t("clearFilters") || "Clear Filters"}</span>
              </Button>
            )}
          </div>

          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={t("search") + "..."}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                className="pl-10"
              />
            </div>

            <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder={t("type")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allTypes")}</SelectItem>
                <SelectItem value="DEPOSIT">{t("deposit")}</SelectItem>
                <SelectItem value="WITHDRAWAL">{t("withdrawal")}</SelectItem>
              </SelectContent>
            </Select>

            <Select value={currencyFilter} onValueChange={(v) => { setCurrencyFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder={t("currency")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allCurrencies") || "All Currencies"}</SelectItem>
                <SelectItem value="PKR">Thai (RS PKR)</SelectItem>
                <SelectItem value="IDR">Indonesian (Rp IDR)</SelectItem>
              </SelectContent>
            </Select>

            <Select value={merchantFilter} onValueChange={(v) => { setMerchantFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder={t("merchant")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allMerchants")}</SelectItem>
                {merchants.map(m => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder={t("status")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allStatus")}</SelectItem>
                <SelectItem value="pending">{t("pending")}</SelectItem>
                <SelectItem value="processing">{t("processing")}</SelectItem>
                <SelectItem value="success">{t("success")}</SelectItem>
                <SelectItem value="declined">{t("declined") || "Declined"}</SelectItem>
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  <span>
                    {dateRange.from ? (
                      dateRange.to ? `${format(dateRange.from, "MMM d")} - ${format(dateRange.to, "MMM d")}` : format(dateRange.from, "MMM d")
                    ) : t("dateRange")}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => { setDateRange(range || { from: null, to: null }); setPage(0); }}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Table */}
        <TransactionTable
          transactions={filteredTransactions}
          isLoading={isLoading}
          onView={setSelectedTx}
          showReseller={false}
          showMerchant={true}
          showProvider={true}
          mixedTypes={true}
        />

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              {t("showing")} {page * pageSize + 1} {t("to")} {Math.min((page + 1) * pageSize, totalTransactions)} {t("of")} {totalTransactions.toLocaleString()} {t("transactions")}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-slate-600">{t("page")} {page + 1} {t("of")} {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        <TransactionDetailPanel
          transaction={selectedTx}
          open={!!selectedTx}
          onClose={() => setSelectedTx(null)}
        />
      </div>
    </ResellerLayout>
  );
}